import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  buildApiUrl,
  getDashboardEnv,
  hydrateDashboardIdentityConfig,
} from '../config/env';
import { configureTokenRefresh } from '../lib/api';
import { createTokenRefreshCoordinator } from '../lib/authRefresh';

export interface TokenState {
  access_token: string;
  refresh_token?: string;
  expires_at: number;
  id_token?: string;
}

type AuthState =
  | { status: 'loading'; error: null; tokens: null }
  | { status: 'unauthenticated'; error: string | null; tokens: null }
  | { status: 'authenticated'; error: null; tokens: TokenState };

type AuthContextValue = AuthState & {
  login(): Promise<void>;
  logout(): void;
};

const authStorageKey = 'billing-zektec.dashboard.auth';
const authPkceStateKey = 'billing-zektec.dashboard.pkce.state';
const authPkceVerifierKey = 'billing-zektec.dashboard.pkce.verifier';
const AuthContext = createContext<AuthContextValue | null>(null);

function readTokens(): TokenState | null {
  try {
    const raw = window.sessionStorage.getItem(authStorageKey);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as TokenState;
  } catch {
    return null;
  }
}

function storeTokens(tokens: TokenState | null) {
  if (tokens) {
    window.sessionStorage.setItem(authStorageKey, JSON.stringify(tokens));
    return;
  }
  window.sessionStorage.removeItem(authStorageKey);
}

function base64UrlEncode(value: Uint8Array) {
  return btoa(String.fromCharCode(...value))
    .replace(/\+/gu, '-')
    .replace(/\//gu, '_')
    .replace(/=+$/u, '');
}

async function sha256(value: string) {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(value),
  );
  return new Uint8Array(digest);
}

function buildRedirectUri() {
  return `${window.location.origin}/auth/callback`;
}

async function exchangeCode(
  code: string,
  verifier: string,
  redirectUri: string,
) {
  const response = await fetch(buildApiUrl('/admin/auth/keycloak/exchange'), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify({
      code,
      code_verifier: verifier,
      redirect_uri: redirectUri,
    }),
  });

  if (!response.ok) {
    throw new Error('keycloak_exchange_failed');
  }

  return normalizeTokenPayload(await response.json());
}

async function refreshKeycloakTokens(
  refreshToken: string,
  previous: TokenState,
) {
  const response = await fetch(buildApiUrl('/admin/auth/keycloak/refresh'), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify({
      refresh_token: refreshToken,
    }),
  });

  if (!response.ok) {
    throw new Error('keycloak_refresh_failed');
  }

  return normalizeTokenPayload(await response.json(), previous);
}

function normalizeTokenPayload(payload: unknown, previous?: TokenState) {
  const tokenPayload = payload as {
    access_token: string;
    expires_in: number;
    refresh_token?: string;
    id_token?: string;
  };

  return {
    access_token: tokenPayload.access_token,
    refresh_token: tokenPayload.refresh_token ?? previous?.refresh_token,
    id_token: tokenPayload.id_token ?? previous?.id_token,
    expires_at: Date.now() + tokenPayload.expires_in * 1000,
  } satisfies TokenState;
}

function clearAuthCallbackParams(url: URL) {
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  url.searchParams.delete('session_state');
  url.searchParams.delete('iss');
  window.history.replaceState({}, '', url.toString());
}

function clearAuthSession() {
  storeTokens(null);
  window.sessionStorage.removeItem(authPkceStateKey);
  window.sessionStorage.removeItem(authPkceVerifierKey);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    error: null,
    tokens: null,
  });
  const refreshCoordinatorRef = useRef<ReturnType<
    typeof createTokenRefreshCoordinator<string, TokenState>
  > | null>(null);

  const refreshAccessToken = useCallback(async (expiredToken?: string) => {
    if (!refreshCoordinatorRef.current) {
      refreshCoordinatorRef.current = createTokenRefreshCoordinator(
        async (refreshExpiredToken?: string) => {
          const existing = readTokens();
          if (!existing?.refresh_token) {
            throw new Error('missing_refresh_token');
          }

          if (
            refreshExpiredToken &&
            existing.access_token !== refreshExpiredToken &&
            existing.expires_at > Date.now() + 10_000
          ) {
            return existing;
          }

          const tokens = await refreshKeycloakTokens(
            existing.refresh_token,
            existing,
          );
          storeTokens(tokens);
          setState({ status: 'authenticated', error: null, tokens });
          return tokens;
        },
      );
    }

    try {
      return await refreshCoordinatorRef.current(expiredToken);
    } catch (error) {
      clearAuthSession();
      setState({
        status: 'unauthenticated',
        error: 'keycloak_refresh_failed',
        tokens: null,
      });
      throw error;
    }
  }, []);

  useEffect(() => {
    configureTokenRefresh(async (expiredToken) => {
      const tokens = await refreshAccessToken(expiredToken);
      return tokens.access_token;
    });

    return () => {
      configureTokenRefresh(null);
    };
  }, [refreshAccessToken]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        await hydrateDashboardIdentityConfig();
      } catch {
        if (!cancelled) {
          setState({
            status: 'unauthenticated',
            error: 'keycloak_exchange_failed',
            tokens: null,
          });
        }
        return;
      }
      if (cancelled) return;
      const url = new URL(window.location.href);
      const code = url.searchParams.get('code');
      const stateParam = url.searchParams.get('state');
      const expectedState = window.sessionStorage.getItem(authPkceStateKey);
      const verifier = window.sessionStorage.getItem(authPkceVerifierKey);

      if (code || stateParam) {
        if (code && stateParam && verifier && expectedState === stateParam) {
          try {
            const tokens = await exchangeCode(
              code,
              verifier,
              buildRedirectUri(),
            );
            if (cancelled) {
              return;
            }
            storeTokens(tokens);
            window.sessionStorage.removeItem(authPkceStateKey);
            window.sessionStorage.removeItem(authPkceVerifierKey);
            clearAuthCallbackParams(url);
            setState({ status: 'authenticated', error: null, tokens });
            return;
          } catch {
            if (!cancelled) {
              clearAuthSession();
              clearAuthCallbackParams(url);
              setState({
                status: 'unauthenticated',
                error: 'keycloak_exchange_failed',
                tokens: null,
              });
            }
            return;
          }
        }

        clearAuthSession();
        clearAuthCallbackParams(url);
      }

      const existing = readTokens();
      if (existing && existing.expires_at > Date.now() + 10_000) {
        setState({ status: 'authenticated', error: null, tokens: existing });
        return;
      }

      if (existing?.refresh_token) {
        try {
          const tokens = await refreshAccessToken(existing.access_token);
          if (!cancelled) {
            setState({ status: 'authenticated', error: null, tokens });
          }
          return;
        } catch {
          if (cancelled) {
            return;
          }
        }
      }

      storeTokens(null);
      setState({ status: 'unauthenticated', error: null, tokens: null });
    }

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [refreshAccessToken]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      async login() {
        try {
          await hydrateDashboardIdentityConfig();
        } catch {
          setState({
            status: 'unauthenticated',
            error: 'keycloak_exchange_failed',
            tokens: null,
          });
          return;
        }
        const dashboardEnv = getDashboardEnv();
        const redirectUri = buildRedirectUri();
        const stateValue = crypto.randomUUID();
        const verifier =
          crypto.randomUUID().replace(/-/gu, '') +
          crypto.randomUUID().replace(/-/gu, '');
        const challenge = base64UrlEncode(await sha256(verifier));
        window.sessionStorage.setItem(authPkceStateKey, stateValue);
        window.sessionStorage.setItem(authPkceVerifierKey, verifier);
        const authUrl = new URL(
          `${dashboardEnv.keycloakIssuerUrl}/protocol/openid-connect/auth`,
        );
        authUrl.searchParams.set('client_id', dashboardEnv.keycloakClientId);
        authUrl.searchParams.set('response_type', 'code');
        authUrl.searchParams.set('scope', 'openid profile email');
        authUrl.searchParams.set('redirect_uri', redirectUri);
        authUrl.searchParams.set('code_challenge', challenge);
        authUrl.searchParams.set('code_challenge_method', 'S256');
        authUrl.searchParams.set('state', stateValue);
        window.location.assign(authUrl.toString());
      },
      logout() {
        const dashboardEnv = getDashboardEnv();
        const idToken =
          state.status === 'authenticated' ? state.tokens.id_token : undefined;
        clearAuthSession();
        const logoutUrl = new URL(
          `${dashboardEnv.keycloakIssuerUrl}/protocol/openid-connect/logout`,
        );
        logoutUrl.searchParams.set(
          'post_logout_redirect_uri',
          `${window.location.origin}/`,
        );
        logoutUrl.searchParams.set('client_id', dashboardEnv.keycloakClientId);
        if (idToken) {
          logoutUrl.searchParams.set('id_token_hint', idToken);
        }
        window.location.assign(logoutUrl.toString());
      },
    }),
    [state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider.');
  }
  return context;
}
